import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Check,
  Eraser,
  FileText,
  FolderOpen,
  History,
  ListChecks,
  MoreHorizontal,
  PanelRightOpen,
  Pencil,
  Plus,
  Search,
  TextCursorInput,
  Undo2,
  Upload,
  WrapText,
  X,
} from 'lucide-react';
import {
  GridIconButton,
  KeyHint,
  SearchField,
  TreeRail,
  WorkspaceBanner,
  WorkspaceButton,
  WorkspaceDataGrid,
  WorkspaceDialog,
  WorkspaceHeader,
  WorkspaceLayout,
  WorkspaceMenu,
  WorkspaceNotice,
  isTypingTarget,
  useRegisterCommands,
  useToast,
  type PaletteCommand,
  type TreeRailCategory,
  type WorkspaceGridColumn,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import {
  conflictReason,
  exactDuplicateNumber,
  requirementLibraryKeys,
  useAddDraftItemMutation,
  useDiscardDraftMutation,
  useLibraryItemsQuery,
  useLibraryTreeQuery,
  useModuleDraftQuery,
  useModuleVersionsQuery,
  usePatchDraftItemMutation,
  usePublishModuleMutation,
  useRenameModuleMutation,
  useSetCategoryStatusMutation,
  useSetModuleStatusMutation,
  type CreatedModule,
  type DraftItemResult,
  type DraftView,
  type LibraryCategory,
  type LibraryModuleSummary,
} from '@/lib/api/requirement-library';
import type { IntakeConfirmResult } from '@/lib/api/requirement-library-intake';
import { AddRequirementDialog, AddRequirementRow } from './add-requirement';
import { confirmedMessage } from './intake/intake-logic';
import { IntakeRunsSheet } from './intake/intake-runs-sheet';
import { IntakeSheet } from './intake/intake-sheet';
import { IntakeSourceViewer, type SourceTarget } from './intake/intake-source-viewer';
import { Highlight } from './highlight';
import { LibraryImportSheet, libraryImportMessage } from './library-import';
import { LibraryPanel } from './library-panel';
import { MetadataTidySheet, metadataTidyMessage } from './metadata-tidy';
import { RenameModuleDialog, RetireCategoryDialog, RetireModuleDialog } from './module-dialogs';
import {
  activeTree,
  applyDraftToRows,
  blankToNull,
  draftChangeCount,
  duplicateFlagLabel,
  formatDate,
  groupKeyOf,
  groupRows,
  matchesSearch,
  matchesV1Number,
  pendingRename,
  plural,
  rowFromItem,
  searchTerms,
  treeCounts,
  unresolvedDuplicateCount,
  type LibraryRow,
} from './library-logic';
import { NewModuleDialog } from './new-module-dialog';
import { PublishDialog } from './publish-dialog';
import { VersionHistorySheet, type HistoryTarget } from './version-history-sheet';

/**
 * Firm settings -> Requirement library (T-0141; E11-F01 browse and search,
 * E11-F02 curation).
 *
 * Design reference: the `#library` screen in `docs/requirement-library/
 * prototype/selection.js` (edit mode, publish dialog, new-module dialog,
 * duplicate flag, side panel). Built from the shared workspace components
 * in `components/ui/workspace` (amendment 01 Decision 28).
 *
 * What the screen is for (Decision 31): maintaining firm content. It has
 * no criticality and no action that adds a requirement to an engagement.
 * Nobody types a prefix or a number (Decision 32); every requirement
 * shows its source (Decision 33).
 *
 * Data: the navigation tree (`GET /requirement-library`) and the whole
 * library in one payload (`GET /requirement-library/items`, revalidated
 * by ETag) so search is instant and client-side. Curation controls are
 * rendered only for callers the server treats as curators; the server
 * refuses the writes for everyone else regardless.
 *
 * Search text and the selected module live in the URL; the route owns
 * them and passes them in, which also keeps this component testable
 * without a router. An empty `module` means no module, the same as an
 * absent one.
 *
 * T-0152 adds, for curators only: retiring a module (with a confirm that
 * says what it does and does not affect), a "Show retired" toggle that
 * lists retired modules so they can be restored, renaming a module in
 * edit mode (a pending change, published with the draft), and creating a
 * category from the new-module dialog.
 *
 * T-0154 adds, for curators only: "Import from a document" (AI document
 * intake, E11-F08) and "Document imports" (its runs) in the header, the
 * source of an AI-extracted requirement in the side panel with a viewer
 * that shows the cited passage, and the kind of match on a duplicate flag.
 *
 * For curators in edit mode, "Tidy appended metadata" moves labelled fields
 * (Tag, Live, Basis, Proof, Pending) off the end of a module's wording into
 * its draft (`MetadataTidySheet`); the side panel shows them as Metadata.
 *
 * T-0157 adds, for curators only: a small menu on each category row of the
 * rail to retire the category (refused while it has active modules, rule 2)
 * or, under "Show retired", restore it; and opening an intake run named in
 * the URL (`openRunId`), which is where the app-wide run watcher's "Open"
 * and the bell's notifications lead.
 */

export interface RequirementLibraryScreenProps {
  /** Search text (URL `q`). */
  query: string;
  /** Selected module id (URL `module`), or null for all modules. */
  moduleId: string | null;
  onQueryChange: (query: string) => void;
  onModuleChange: (moduleId: string | null) => void;
  /**
   * An intake run to open in the intake sheet (URL `intake_run`, T-0157).
   * `onOpenRunHandled` is called once it is open, so the route can drop it
   * from the URL.
   */
  openRunId?: string | null;
  onOpenRunHandled?: () => void;
}

const ALL_ID = '__all__';

interface ModuleEntry extends LibraryModuleSummary {
  category_id: string;
  category_name: string;
}

const getRowId = (row: LibraryRow) => row.item_id;
const STALE_MESSAGE =
  'This draft was changed by another curator. It has been reloaded: check it, then try again.';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function RequirementLibraryScreen({
  query,
  moduleId: moduleIdParam,
  onQueryChange,
  onModuleChange,
  openRunId = null,
  onOpenRunHandled,
}: RequirementLibraryScreenProps) {
  // `?module=` (empty) is "all modules", exactly like no `module` at all.
  const moduleId = blankToNull(moduleIdParam);
  const qc = useQueryClient();
  const { toast } = useToast();
  // Retired modules are listed only when a curator asks for them.
  const [showRetired, setShowRetired] = React.useState(false);
  const treeQuery = useLibraryTreeQuery(showRetired);
  const itemsQuery = useLibraryItemsQuery();
  const tree = treeQuery.data;

  const modules = React.useMemo<ModuleEntry[]>(
    () =>
      (tree?.categories ?? []).flatMap((c) =>
        c.modules.map((m) => ({ ...m, category_id: c.id, category_name: c.name })),
      ),
    [tree],
  );
  const selectedModule = moduleId ? (modules.find((m) => m.id === moduleId) ?? null) : null;

  // Curation authority, as the server states it. It only decides which
  // controls to render: the server refuses the writes for everyone else.
  const isCurator = tree?.can_curate === true;
  const retiredSelected = selectedModule?.status === 'retired';

  // ---- view state ---------------------------------------------------------
  const [editingModuleId, setEditingModuleId] = React.useState<string | null>(null);
  const editing =
    isCurator &&
    selectedModule != null &&
    !retiredSelected &&
    editingModuleId === selectedModule.id;
  React.useEffect(() => {
    // Edit mode belongs to one module; moving away leaves it.
    setEditingModuleId((current) => (current != null && current !== moduleId ? null : current));
  }, [moduleId]);

  const [wrap, setWrap] = React.useState(false);
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [panelId, setPanelId] = React.useState<string | null>(null);
  const [history, setHistory] = React.useState<HistoryTarget | null>(null);
  const [newModuleOpen, setNewModuleOpen] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [retireOpen, setRetireOpen] = React.useState(false);
  const [retireError, setRetireError] = React.useState<string | null>(null);
  const [retiringCategory, setRetiringCategory] = React.useState<LibraryCategory | null>(null);
  const [retireCategoryError, setRetireCategoryError] = React.useState<string | null>(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameError, setRenameError] = React.useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [tidyOpen, setTidyOpen] = React.useState(false);
  /** The AI document intake sheet: a new import (`runId` null) or one reopened. */
  const [intake, setIntake] = React.useState<{ runId: string | null } | null>(null);
  const [runsOpen, setRunsOpen] = React.useState(false);
  const [source, setSource] = React.useState<SourceTarget | null>(null);
  const [addingSub, setAddingSub] = React.useState<string | null>(null);
  const [addError, setAddError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [publishError, setPublishError] = React.useState<string | null>(null);
  const [stale, setStale] = React.useState(false);
  const [pendingWrites, setPendingWrites] = React.useState(0);
  const busy = pendingWrites > 0;
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Instant search: the input updates at once, the filter follows.
  const deferredQuery = React.useDeferredValue(query);
  const terms = React.useMemo(() => searchTerms(deferredQuery), [deferredQuery]);

  // ---- data ---------------------------------------------------------------
  const draftQuery = useModuleDraftQuery(selectedModule?.id ?? null, editing);
  const draft = editing ? draftQuery.data : undefined;
  const versionsQuery = useModuleVersionsQuery(
    selectedModule && selectedModule.current_version > 0 ? selectedModule.id : null,
  );

  const allRows = React.useMemo(
    () => (itemsQuery.data?.items ?? []).map(rowFromItem),
    [itemsQuery.data],
  );

  const scopeRows = React.useMemo(() => {
    if (!selectedModule) return allRows;
    const published = allRows.filter((r) => r.module_id === selectedModule.id);
    if (!editing) return published;
    return applyDraftToRows(published, draft, {
      module_id: selectedModule.id,
      module_name: selectedModule.name,
      category_name: selectedModule.category_name,
    });
  }, [allRows, selectedModule, editing, draft]);

  const rows = React.useMemo(
    () => groupRows(scopeRows.filter((r) => matchesSearch(r, terms))),
    [scopeRows, terms],
  );

  // Counts are of what the library offers: retired modules are listed (when
  // asked for) with their own count but never add to a category or the total.
  const counts = React.useMemo(
    () => (tree ? treeCounts(activeTree(tree), allRows, terms) : null),
    [tree, allRows, terms],
  );

  const categoryStatusMutation = useSetCategoryStatusMutation();

  const restoreCategory = React.useCallback(
    (target: LibraryCategory) => {
      setSaveError(null);
      categoryStatusMutation.mutate(
        { categoryId: target.id, action: 'restore' },
        {
          onSuccess: () =>
            toast({
              message: `${target.name} restored. Modules can be created in it and restored into it again.`,
            }),
          onError: (err) =>
            setSaveError(
              `${target.name} was not restored: ${errorMessage(err, 'the change was refused.')}`,
            ),
        },
      );
    },
    [categoryStatusMutation, toast],
  );

  const railCategories = React.useMemo<TreeRailCategory[]>(
    () =>
      (tree?.categories ?? []).map((c) => ({
        id: c.id,
        label: c.status === 'retired' ? `${c.name} (retired)` : c.name,
        count: counts?.byCategory.get(c.id) ?? 0,
        actions: isCurator ? (
          <CategoryMenu
            category={c}
            onRetire={() => {
              setRetireCategoryError(null);
              setRetiringCategory(c);
            }}
            onRestore={() => restoreCategory(c)}
          />
        ) : undefined,
        items: c.modules.map((m) =>
          m.status === 'retired'
            ? {
                id: m.id,
                label: `${m.name} (retired)`,
                count: terms.length > 0 ? 0 : m.item_count,
                icon: <Archive className="h-3.5 w-3.5" aria-hidden="true" />,
              }
            : {
                id: m.id,
                label: m.name,
                count: counts?.byModule.get(m.id) ?? 0,
                dot: m.has_draft === true,
                dotLabel: 'Unpublished draft',
              },
        ),
      })),
    [tree, counts, terms, isCurator, restoreCategory],
  );

  const moduleNameById = React.useMemo(
    () => new Map(modules.map((m) => [m.id, m.name])),
    [modules],
  );

  const panelRow = React.useMemo(
    () =>
      panelId
        ? (scopeRows.find((r) => r.item_id === panelId) ??
          allRows.find((r) => r.item_id === panelId) ??
          null)
        : null,
    [panelId, scopeRows, allRows],
  );
  const panelModule = panelRow ? (modules.find((m) => m.id === panelRow.module_id) ?? null) : null;

  const draftReady = editing && draft != null;
  const changeCount = draftChangeCount(draft);
  const openDuplicates = unresolvedDuplicateCount(draft);
  const renamedTo = pendingRename(draft);

  // ---- draft writes -------------------------------------------------------
  const addMutation = useAddDraftItemMutation();
  const patchMutation = usePatchDraftItemMutation();
  const discardMutation = useDiscardDraftMutation();
  const publishMutation = usePublishModuleMutation();
  const renameMutation = useRenameModuleMutation();
  const statusMutation = useSetModuleStatusMutation();

  // Writes run one at a time, each against the revision the last one left,
  // so two quick edits never race each other into a stale-revision refusal.
  const queueRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const runWrite = React.useCallback(
    <T,>(targetModuleId: string, fn: (baseRevision: number) => Promise<T>): Promise<T> => {
      setPendingWrites((n) => n + 1);
      const task = queueRef.current.then(() => {
        const current = qc.getQueryData<DraftView>(requirementLibraryKeys.draft(targetModuleId));
        if (!current) throw new Error('The draft is still loading. Try again in a moment.');
        return fn(current.draft_revision);
      });
      queueRef.current = task.then(
        () => undefined,
        () => undefined,
      );
      return task.finally(() => setPendingWrites((n) => n - 1));
    },
    [qc],
  );

  /** True when the error was a stale draft and has been dealt with. */
  const handleStale = React.useCallback(
    (err: unknown): boolean => {
      if (conflictReason(err) !== 'stale_revision') return false;
      setStale(true);
      toast({ message: STALE_MESSAGE });
      return true;
    },
    [toast],
  );

  const afterItemWrite = React.useCallback((result: DraftItemResult) => {
    setSaveError(null);
    setStale(false);
    // A look-alike was found: open the panel, which shows it and the choice.
    if (result.duplicates.length > 0 && result.op !== null) {
      setFocusedId(result.item_id);
      setPanelId(result.item_id);
    }
  }, []);

  const patchItem = React.useCallback(
    (
      row: LibraryRow,
      change: {
        text?: string;
        subcategory?: string;
        op?: 'retire' | 'restore';
        keptBothWith?: string[];
      },
    ) => {
      runWrite(row.module_id, (baseRevision) =>
        patchMutation.mutateAsync({
          moduleId: row.module_id,
          itemId: row.item_id,
          baseRevision,
          ...change,
        }),
      ).then(afterItemWrite, (err: unknown) => {
        if (handleStale(err)) return;
        const existing = exactDuplicateNumber(err);
        setSaveError(
          existing
            ? `${row.number} was not saved: this module already has a requirement with the same text, ${existing}. Edit ${existing} instead of keeping a second copy.`
            : `${row.number} was not saved: ${errorMessage(err, 'the change was refused.')}`,
        );
      });
    },
    // `mutateAsync` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runWrite, afterItemWrite, handleStale],
  );

  const commitText = React.useCallback(
    (row: LibraryRow, value: string) => {
      const text = value.trim();
      if (!text) {
        toast({ message: 'A requirement cannot be blank. Retire it instead.' });
        return;
      }
      if (text !== row.text) patchItem(row, { text });
    },
    [patchItem, toast],
  );

  const retire = React.useCallback(
    (row: LibraryRow) => patchItem(row, { op: 'retire' }),
    [patchItem],
  );
  const restore = React.useCallback(
    (row: LibraryRow) => patchItem(row, { op: 'restore' }),
    [patchItem],
  );
  const keepBoth = React.useCallback(
    (row: LibraryRow) => patchItem(row, { keptBothWith: row.duplicates.map((d) => d.item_id) }),
    [patchItem],
  );
  const discardDuplicate = React.useCallback(
    (row: LibraryRow) => {
      // A reworded requirement goes back to its published wording; a new
      // one is withdrawn from the draft.
      if (row.change === 'reworded' && row.previous) {
        patchItem(row, { text: row.previous.text, subcategory: row.previous.subcategory });
      } else {
        patchItem(row, { op: 'retire' });
      }
    },
    [patchItem],
  );

  const addRequirement = (subcategory: string, text: string, from: 'row' | 'dialog') => {
    if (!selectedModule) return;
    const target = selectedModule.id;
    setAddError(null);
    runWrite(target, (baseRevision) =>
      addMutation.mutateAsync({ moduleId: target, subcategory, text, baseRevision }),
    ).then(
      (result) => {
        afterItemWrite(result);
        setAddingSub(null);
        setAddDialogOpen(false);
        setFocusedId(result.item_id);
        toast({ message: `${result.number} added to the draft.` });
      },
      (err: unknown) => {
        if (handleStale(err)) {
          setAddError(STALE_MESSAGE);
          return;
        }
        const existing = exactDuplicateNumber(err);
        setAddError(
          existing
            ? `This module already has a requirement with the same text: ${existing}. Edit ${existing} instead of adding a second copy.`
            : errorMessage(err, 'The requirement could not be added.'),
        );
        if (from === 'row') setAddingSub(subcategory);
      },
    );
  };

  const discardDraft = () => {
    if (!selectedModule) return;
    const target = selectedModule.id;
    runWrite(target, (baseRevision) =>
      discardMutation.mutateAsync({ moduleId: target, baseRevision }),
    ).then(
      () => {
        setDiscardOpen(false);
        setSaveError(null);
        setStale(false);
        toast({ message: `Draft of ${selectedModule.name} discarded.` });
      },
      (err: unknown) => {
        setDiscardOpen(false);
        if (!handleStale(err)) setSaveError(errorMessage(err, 'The draft could not be discarded.'));
      },
    );
  };

  const publish = (changeNote: string) => {
    if (!selectedModule) return;
    const target = selectedModule;
    setPublishError(null);
    runWrite(target.id, (baseRevision) =>
      publishMutation.mutateAsync({ moduleId: target.id, changeNote, baseRevision }),
    ).then(
      (result) => {
        setPublishOpen(false);
        setEditingModuleId(null);
        setStale(false);
        setSaveError(null);
        const publishedName = result.module_name ?? target.name;
        toast({
          message:
            result.version > 1
              ? `${publishedName} v${result.version} published. Engagements on earlier versions are unchanged.`
              : `${publishedName} v1 published. Engagements can now add it.`,
        });
      },
      (err: unknown) => {
        // The dialog stays open on the reloaded draft so the curator sees
        // what changed before publishing again.
        if (conflictReason(err) === 'stale_revision') setStale(true);
        else setPublishError(errorMessage(err, 'The module could not be published.'));
      },
    );
  };

  /** Propose a new name in the draft; the published name withdraws a pending rename. */
  const renameModule = (name: string | null) => {
    if (!selectedModule) return;
    const target = selectedModule;
    setRenameError(null);
    runWrite(target.id, (baseRevision) =>
      renameMutation.mutateAsync({ moduleId: target.id, name, baseRevision }),
    ).then(
      (result) => {
        setRenameOpen(false);
        setSaveError(null);
        setStale(false);
        toast({
          message: result.pending_name
            ? `${target.name} will be renamed to ${result.pending_name} when you publish.`
            : `Rename withdrawn. The module stays ${target.name}.`,
        });
      },
      (err: unknown) => {
        const message = handleStale(err)
          ? STALE_MESSAGE
          : errorMessage(err, 'The module could not be renamed.');
        if (renameOpen) setRenameError(message);
        else if (conflictReason(err) !== 'stale_revision') setSaveError(message);
      },
    );
  };

  const retireModule = () => {
    if (!selectedModule) return;
    const target = selectedModule;
    setRetireError(null);
    statusMutation.mutate(
      { moduleId: target.id, action: 'retire' },
      {
        onSuccess: () => {
          setRetireOpen(false);
          setEditingModuleId(null);
          // Show it where it now lives, so it can be restored from there.
          setShowRetired(true);
          toast({
            message: `${target.name} retired. It is listed under "Show retired" and can be restored.`,
          });
        },
        onError: (err) => setRetireError(errorMessage(err, 'The module could not be retired.')),
      },
    );
  };

  const restoreModule = (target: { id: string; name: string }) => {
    setSaveError(null);
    statusMutation.mutate(
      { moduleId: target.id, action: 'restore' },
      {
        onSuccess: () =>
          toast({ message: `${target.name} restored. Engagements can add it again.` }),
        onError: (err) =>
          setSaveError(
            `${target.name} was not restored: ${errorMessage(err, 'the change was refused.')}`,
          ),
      },
    );
  };

  const retireCategory = () => {
    const target = retiringCategory;
    if (!target) return;
    setRetireCategoryError(null);
    categoryStatusMutation.mutate(
      { categoryId: target.id, action: 'retire' },
      {
        onSuccess: () => {
          setRetiringCategory(null);
          // Show it where it now lives, so it can be restored from there.
          setShowRetired(true);
          toast({
            message: `${target.name} retired. It is listed under "Show retired" and can be restored.`,
          });
        },
        onError: (err) =>
          setRetireCategoryError(errorMessage(err, 'The category could not be retired.')),
      },
    );
  };

  // ---- navigation helpers -------------------------------------------------
  const selectModule = React.useCallback(
    (id: string | null) => {
      setPanelId(null);
      setFocusedId(null);
      setAddingSub(null);
      setSaveError(null);
      setStale(false);
      onModuleChange(id);
    },
    [onModuleChange],
  );

  const startEditing = React.useCallback(() => {
    if (!selectedModule || selectedModule.status === 'retired') return;
    setSaveError(null);
    setStale(false);
    setEditingModuleId(selectedModule.id);
  }, [selectedModule]);

  const onModuleCreated = (module: CreatedModule) => {
    onQueryChange('');
    selectModule(module.id);
    setEditingModuleId(module.id);
  };

  const openIntake = React.useCallback((runId: string | null = null) => {
    setRunsOpen(false);
    setIntake({ runId });
  }, []);
  const openRuns = React.useCallback(() => {
    setIntake(null);
    setRunsOpen(true);
  }, []);

  // T-0157: a run named in the URL (the run watcher's "Open", a bell
  // notification) opens in the intake sheet once the caller is known to be
  // a curator; then the route drops it from the URL.
  React.useEffect(() => {
    if (!openRunId || !isCurator) return;
    openIntake(openRunId);
    onOpenRunHandled?.();
  }, [openRunId, isCurator, openIntake, onOpenRunHandled]);

  /** Sub-categories a module already has, offered when a candidate is filed there. */
  const subcategoriesFor = React.useCallback(
    (id: string) =>
      Array.from(new Set(allRows.filter((r) => r.module_id === id).map((r) => r.subcategory))),
    [allRows],
  );

  /**
   * An AI document intake was confirmed: its accepted candidates are in
   * their modules' drafts. Open the first of those modules in edit mode;
   * the others carry the draft marker in the tree.
   */
  const onIntakeConfirmed = (result: IntakeConfirmResult) => {
    setIntake(null);
    void qc.invalidateQueries({ queryKey: requirementLibraryKeys.all });
    const first = result.modules[0];
    if (first) {
      onQueryChange('');
      selectModule(first.module_id);
      setEditingModuleId(first.module_id);
      // Show the first new requirement with its source in the side panel.
      const added = result.items.find((i) => i.module_id === first.module_id);
      if (added) {
        setFocusedId(added.item_id);
        setPanelId(added.item_id);
      }
    }
    toast({
      message: confirmedMessage(result.added, result.modules.length),
      durationMs: 12_000,
    });
  };

  const openExistingModule = (id: string) => {
    void qc.invalidateQueries({ queryKey: requirementLibraryKeys.all });
    onQueryChange('');
    selectModule(id);
  };

  // "/" focuses search, as the footer hint says.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ---- command palette ----------------------------------------------------
  const staticCommands = React.useMemo<PaletteCommand[]>(() => {
    const list: PaletteCommand[] = modules.map((m) => ({
      id: `requirement-library:module:${m.id}`,
      group: 'Requirement library modules',
      label: m.name,
      keywords: [m.category_name, m.prefix, 'go to module'],
      icon: <FolderOpen className="h-3.5 w-3.5" />,
      hint: m.current_version > 0 ? plural(m.item_count, 'requirement') : 'Not yet published',
      run: () => selectModule(m.id),
    }));
    if (isCurator) {
      list.push({
        id: 'requirement-library:new-module',
        group: 'Requirement library',
        label: 'New module',
        keywords: ['create', 'add module'],
        icon: <Plus className="h-3.5 w-3.5" />,
        run: () => setNewModuleOpen(true),
      });
      list.push({
        id: 'requirement-library:import-document',
        group: 'Requirement library',
        label: 'Import from a document',
        keywords: ['ai', 'extract', 'rfp', 'pdf', 'word', 'upload', 'intake'],
        icon: <FileText className="h-3.5 w-3.5" />,
        run: () => openIntake(),
      });
      list.push({
        id: 'requirement-library:document-imports',
        group: 'Requirement library',
        label: 'Document imports',
        keywords: ['ai', 'runs', 'history', 'source documents', 'withdraw', 'intake'],
        icon: <ListChecks className="h-3.5 w-3.5" />,
        run: openRuns,
      });
      if (selectedModule && draftReady) {
        list.push({
          id: 'requirement-library:import-spreadsheet',
          group: 'Requirement library',
          label: `Import a spreadsheet into ${selectedModule.name}`,
          keywords: ['import', 'upload', 'xlsx', 'csv', 'excel', 'bulk'],
          icon: <Upload className="h-3.5 w-3.5" />,
          run: () => setImportOpen(true),
        });
        list.push({
          id: 'requirement-library:tidy-metadata',
          group: 'Requirement library',
          label: `Tidy appended metadata in ${selectedModule.name}`,
          keywords: ['tag', 'live', 'basis', 'proof', 'pending', 'clean', 'register', 'metadata'],
          icon: <Eraser className="h-3.5 w-3.5" />,
          run: () => setTidyOpen(true),
        });
      }
      if (selectedModule && !editing && !retiredSelected) {
        list.push({
          id: 'requirement-library:edit-module',
          group: 'Requirement library',
          label: `Edit module: ${selectedModule.name}`,
          keywords: ['edit module', 'draft', 'curate'],
          icon: <Pencil className="h-3.5 w-3.5" />,
          run: startEditing,
        });
      }
    }
    return list;
  }, [
    modules,
    isCurator,
    selectedModule,
    editing,
    draftReady,
    retiredSelected,
    selectModule,
    startEditing,
    openIntake,
    openRuns,
  ]);
  useRegisterCommands(staticCommands);

  const findCommands = React.useCallback(
    (q: string): PaletteCommand[] => {
      const t = searchTerms(q);
      if (t.length === 0) return [];
      const found: PaletteCommand[] = [];
      for (const row of allRows) {
        if (!matchesSearch(row, t)) continue;
        found.push({
          id: `requirement-library:item:${row.item_id}`,
          group: 'Requirements',
          mono: row.number,
          label: row.text,
          hint: row.v1_number ? `${row.module_name} · v1 ${row.v1_number}` : row.module_name,
          run: () => {
            onQueryChange('');
            selectModule(row.module_id);
            setFocusedId(row.item_id);
            setPanelId(row.item_id);
          },
        });
        if (found.length >= 8) break;
      }
      return found;
    },
    [allRows, onQueryChange, selectModule],
  );
  useRegisterCommands(findCommands);

  // ---- grid ---------------------------------------------------------------
  const columns = React.useMemo<WorkspaceGridColumn<LibraryRow>[]>(
    () => [
      {
        key: 'number',
        header: 'No.',
        width: '112px',
        cell: (r) => (
          <span className="font-mono text-meta font-medium text-ink-700">
            <Highlight text={r.number} terms={terms} />
          </span>
        ),
      },
      {
        key: 'text',
        header: 'Requirement',
        editValue: (r) => r.text,
        editLabel: 'Requirement text',
        cell: (r) => (
          <span
            className={r.change === 'retired' ? 'text-ink-300 line-through' : 'text-ink-900'}
            title={editing && r.change !== 'retired' ? 'Double-click to edit' : undefined}
          >
            {r.duplicates.length > 0 ? (
              <span className="mr-2 inline-flex h-[22px] items-center rounded-full bg-posture-at-risk-tint px-2.5 align-middle text-meta font-medium text-posture-at-risk no-underline">
                {duplicateFlagLabel(r.duplicates[0]!)}
              </span>
            ) : null}
            {matchesV1Number(r.v1_number, terms) ? (
              // The search hit the old Caliber v1 number: show it, marked.
              <span className="mr-2 font-mono text-meta text-ink-500">
                v1{' '}
                <mark className="rounded-[2px] bg-cite-highlight px-px text-inherit">
                  {r.v1_number}
                </mark>
              </span>
            ) : null}
            <Highlight text={r.text} terms={terms} />
          </span>
        ),
      },
    ],
    [terms, editing],
  );

  const groupBy = React.useCallback((r: LibraryRow) => groupKeyOf(r), []);
  const showModuleInGroup = selectedModule == null;
  const renderGroup = React.useCallback(
    (key: string, count: number) => {
      const [modId = '', sub = ''] = key.split('\u0000');
      return (
        <>
          {showModuleInGroup ? (
            <span className="mr-1 truncate font-normal text-ink-500">
              {moduleNameById.get(modId) ?? ''} ›
            </span>
          ) : null}
          <span className="truncate">
            <Highlight text={sub} terms={terms} />
          </span>
          <span className="ml-2 font-mono font-normal text-ink-500">{count}</span>
        </>
      );
    },
    [showModuleInGroup, moduleNameById, terms],
  );

  const showAddRows = draftReady && terms.length === 0;
  const renderGroupFooter = React.useCallback(
    (key: string) => {
      const sub = key.split('\u0000')[1] ?? '';
      return (
        <AddRequirementRow
          subcategory={sub}
          active={addingSub === sub}
          busy={busy}
          error={addingSub === sub ? addError : null}
          onStart={() => {
            setAddError(null);
            setAddingSub(sub);
          }}
          onCancel={() => {
            setAddError(null);
            setAddingSub(null);
          }}
          onSubmit={(text) => addRequirement(sub, text, 'row')}
        />
      );
    },
    // `addRequirement` closes over the selected module only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addingSub, addError, busy, selectedModule],
  );

  // In edit mode a click only focuses the row (so a double-click can reach
  // the text under a floating panel); this button, or Enter, opens it.
  const rowActions = React.useCallback(
    (r: LibraryRow) => (
      <>
        <GridIconButton
          aria-label={`Open ${r.number}`}
          title="Open details"
          onClick={() => {
            setFocusedId(r.item_id);
            setPanelId(r.item_id);
          }}
        >
          <PanelRightOpen className="h-3.5 w-3.5" aria-hidden="true" />
        </GridIconButton>
        {r.change === 'retired' ? (
          <GridIconButton
            aria-label={`Keep ${r.number}`}
            title="Keep this requirement"
            onClick={() => restore(r)}
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
          </GridIconButton>
        ) : (
          <GridIconButton
            aria-label={`Retire ${r.number}`}
            title={r.change === 'added' ? 'Remove from draft' : 'Retire on publish'}
            onClick={() => retire(r)}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </GridIconButton>
        )}
      </>
    ),
    [retire, restore],
  );

  const rowClassName = React.useCallback(
    (r: LibraryRow) => (r.change ? 'shadow-[inset_2px_0_0_var(--accent-amber-600)]' : undefined),
    [],
  );
  const isRowEditable = React.useCallback((r: LibraryRow) => r.change !== 'retired', []);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;
  const onEditCommit = React.useCallback(
    (rowId: string, _columnKey: string, value: string) => {
      const row = rowsRef.current.find((r) => r.item_id === rowId);
      if (row) commitText(row, value);
    },
    [commitText],
  );
  const openRow = React.useCallback((id: string) => setPanelId(id), []);
  const focusRow = React.useCallback((id: string) => {
    setFocusedId(id);
    // Like the prototype: once the panel is open it follows row focus.
    setPanelId((open) => (open ? id : open));
  }, []);

  // ---- states before the workspace can render -----------------------------
  const loadError = treeQuery.error ?? itemsQuery.error;
  if (loadError) {
    const denied = loadError instanceof ApiError && loadError.code === 'authorization';
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        {denied ? (
          <>
            <h1 className="text-h2 font-semibold text-ink-900">Requirement library</h1>
            <p className="max-w-md text-body-sm text-ink-500">
              You do not have access to the requirement library. Ask a firm administrator if you
              need it.
            </p>
          </>
        ) : (
          <>
            <ErrorMessage error={loadError} />
            <WorkspaceButton
              onClick={() => {
                void treeQuery.refetch();
                void itemsQuery.refetch();
              }}
            >
              Try again
            </WorkspaceButton>
          </>
        )}
      </div>
    );
  }
  if (!tree || !itemsQuery.data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the requirement library…
      </div>
    );
  }

  // ---- header -------------------------------------------------------------
  const currentVersion = versionsQuery.data?.versions.find((v) => v.is_current) ?? null;
  const meta = selectedModule
    ? retiredSelected
      ? `${selectedModule.category_name} · retired${
          selectedModule.current_version > 0
            ? ` · last published as version ${selectedModule.current_version}`
            : ' · never published'
        }`
      : selectedModule.current_version > 0
        ? `${selectedModule.category_name} · version ${selectedModule.current_version} · published ${formatDate(
            currentVersion?.published_at ?? selectedModule.published_at,
          )}${currentVersion?.published_by.name ? ` by ${currentVersion.published_by.name}` : ''}`
        : `${selectedModule.category_name} · not yet published`
    : `Firm-standard requirements that engagements load from. ${plural(tree.item_count, 'requirement')} · ${plural(tree.module_count, 'module')} · ${plural(tree.categories.length, 'category', 'categories')}`;

  const header = (
    <WorkspaceHeader
      title={selectedModule ? selectedModule.name : 'Requirement library'}
      meta={meta}
      actions={
        <>
          {selectedModule && selectedModule.current_version > 0 ? (
            <WorkspaceButton
              size="md"
              onClick={() =>
                setHistory({ moduleId: selectedModule.id, view: selectedModule.current_version })
              }
            >
              <History className="h-3.5 w-3.5" aria-hidden="true" />
              Version history
            </WorkspaceButton>
          ) : null}
          {isCurator ? (
            editing ? (
              <>
                <WorkspaceButton
                  size="md"
                  disabled={!draftReady || busy}
                  onClick={() => {
                    setRenameError(null);
                    setRenameOpen(true);
                  }}
                >
                  <TextCursorInput className="h-3.5 w-3.5" aria-hidden="true" />
                  Rename
                </WorkspaceButton>
                <WorkspaceButton
                  size="md"
                  disabled={!draftReady || busy}
                  onClick={() => {
                    setRetireError(null);
                    setRetireOpen(true);
                  }}
                >
                  <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                  Retire module
                </WorkspaceButton>
                <WorkspaceButton size="md" onClick={() => setEditingModuleId(null)}>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Done editing
                </WorkspaceButton>
              </>
            ) : (
              <>
                <WorkspaceButton size="md" onClick={openRuns}>
                  <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
                  Document imports
                </WorkspaceButton>
                <WorkspaceButton size="md" onClick={() => openIntake()}>
                  <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                  Import from a document
                </WorkspaceButton>
                <WorkspaceButton size="md" onClick={() => setNewModuleOpen(true)}>
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  New module
                </WorkspaceButton>
                {selectedModule && retiredSelected ? (
                  <WorkspaceButton
                    size="md"
                    variant="primary"
                    disabled={statusMutation.isPending}
                    onClick={() => restoreModule(selectedModule)}
                  >
                    <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
                    {statusMutation.isPending ? 'Restoring…' : 'Restore module'}
                  </WorkspaceButton>
                ) : (
                  <WorkspaceButton
                    size="md"
                    variant="primary"
                    disabled={!selectedModule}
                    title={selectedModule ? undefined : 'Select a module to edit it'}
                    onClick={startEditing}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    Edit module
                  </WorkspaceButton>
                )}
              </>
            )
          ) : null}
        </>
      }
    />
  );

  // ---- banner (edit mode) -------------------------------------------------
  const nextVersion = (selectedModule?.current_version ?? 0) + 1;
  const banner =
    editing && selectedModule ? (
      <WorkspaceBanner
        actions={
          changeCount > 0 ? (
            <>
              <WorkspaceButton
                variant="tertiary"
                disabled={busy}
                onClick={() => setDiscardOpen(true)}
              >
                Discard draft
              </WorkspaceButton>
              <WorkspaceButton
                variant="primary"
                disabled={busy}
                onClick={() => {
                  setPublishError(null);
                  setPublishOpen(true);
                }}
              >
                Review and publish v{nextVersion}
              </WorkspaceButton>
            </>
          ) : undefined
        }
      >
        {draftQuery.isError ? (
          <>
            <strong className="font-semibold text-ink-900">The draft could not be loaded.</strong>{' '}
            {errorMessage(draftQuery.error, '')}{' '}
            <button
              type="button"
              className="font-medium underline underline-offset-[3px]"
              onClick={() => void draftQuery.refetch()}
            >
              Try again
            </button>
          </>
        ) : !draft ? (
          <>Loading the draft of {selectedModule.name}…</>
        ) : changeCount > 0 ? (
          <>
            <strong className="font-semibold text-ink-900">Draft of {selectedModule.name}</strong> —{' '}
            {plural(changeCount, 'unpublished change')}
            {openDuplicates > 0 ? `, ${openDuplicates} flagged as a possible duplicate` : ''}.{' '}
            {renamedTo != null ? (
              <>
                Publishing renames the module to{' '}
                <strong className="font-semibold text-ink-900">{renamedTo}</strong>.{' '}
                <button
                  type="button"
                  disabled={busy}
                  className="font-medium underline underline-offset-[3px]"
                  onClick={() => renameModule(null)}
                >
                  Undo rename
                </button>{' '}
              </>
            ) : null}
            Only curators can see this draft; engagements are not affected.
          </>
        ) : (
          <>
            <strong className="font-semibold text-ink-900">Editing {selectedModule.name}.</strong>{' '}
            Double-click a requirement to reword it, add new ones at the end of each sub-category,
            or retire what is obsolete. Changes stay in a private draft until you publish.
          </>
        )}
      </WorkspaceBanner>
    ) : null;

  // ---- toolbar ------------------------------------------------------------
  const toolbar = (
    <>
      <SearchField
        ref={searchRef}
        value={query}
        onChange={onQueryChange}
        aria-label="Search the requirement library"
        wrapperClassName="w-auto min-w-[180px] max-w-80 flex-1"
        placeholder="Search number, module or text…"
        icon={<Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />}
        hint={<kbd className="kbd">/</kbd>}
      />
      <span className="text-meta text-ink-500" aria-live="polite">
        {rows.length.toLocaleString()} shown
      </span>
      <div className="ml-auto flex items-center gap-2">
        {draftReady ? (
          <>
            <WorkspaceButton
              onClick={() => {
                setAddError(null);
                setAddDialogOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add requirement
            </WorkspaceButton>
            <WorkspaceButton disabled={busy} onClick={() => setImportOpen(true)}>
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              Import a spreadsheet
            </WorkspaceButton>
            <WorkspaceButton
              disabled={busy}
              title="Move Tag, Live, Basis, Proof and Pending off the end of the wording"
              onClick={() => setTidyOpen(true)}
            >
              <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
              Tidy appended metadata
            </WorkspaceButton>
          </>
        ) : null}
        {isCurator ? (
          <WorkspaceButton
            aria-pressed={showRetired}
            title="List retired categories and modules so they can be restored"
            onClick={() => setShowRetired((s) => !s)}
          >
            <Archive className="h-3.5 w-3.5" aria-hidden="true" />
            {showRetired ? 'Hide retired' : 'Show retired'}
          </WorkspaceButton>
        ) : null}
        <WorkspaceButton aria-pressed={wrap} onClick={() => setWrap((w) => !w)}>
          <WrapText className="h-3.5 w-3.5" aria-hidden="true" />
          {wrap ? 'Single line' : 'Wrap text'}
        </WorkspaceButton>
      </div>
    </>
  );

  // ---- main content -------------------------------------------------------
  const trimmedQuery = query.trim();
  let emptyState: React.ReactNode;
  if (terms.length > 0) {
    emptyState = (
      <EmptyInline title="No requirements to show">
        Nothing matches &ldquo;{trimmedQuery}&rdquo;
        {selectedModule ? ` in ${selectedModule.name}` : ''}.
        <div className="mt-3 flex justify-center gap-2">
          <WorkspaceButton onClick={() => onQueryChange('')}>Clear search</WorkspaceButton>
          {selectedModule ? (
            <WorkspaceButton onClick={() => selectModule(null)}>Search all modules</WorkspaceButton>
          ) : null}
        </div>
      </EmptyInline>
    );
  } else if (selectedModule && editing) {
    emptyState = (
      <EmptyInline title={`${selectedModule.name} has no requirements yet`}>
        Add the first one, then publish the module to make it available to engagements.
        {draftReady ? (
          <div className="mt-3">
            <WorkspaceButton
              variant="primary"
              onClick={() => {
                setAddError(null);
                setAddDialogOpen(true);
              }}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add requirement
            </WorkspaceButton>
          </div>
        ) : null}
      </EmptyInline>
    );
  } else if (selectedModule) {
    emptyState = (
      <EmptyInline
        title={
          selectedModule.current_version > 0
            ? `${selectedModule.name} has no requirements`
            : `${selectedModule.name} has not been published yet`
        }
      >
        {isCurator
          ? 'Choose Edit module to add requirements, then publish.'
          : 'Nothing has been published in this module.'}
      </EmptyInline>
    );
  } else {
    emptyState = (
      <EmptyInline title="The requirement library is empty">
        {isCurator
          ? 'Create the first module to start the library.'
          : 'No requirements have been published yet.'}
        {isCurator ? (
          <div className="mt-3">
            <WorkspaceButton variant="primary" onClick={() => setNewModuleOpen(true)}>
              <Plus className="h-3 w-3" aria-hidden="true" />
              New module
            </WorkspaceButton>
          </div>
        ) : null}
      </EmptyInline>
    );
  }

  const unknownModule = moduleId != null && selectedModule == null;

  return (
    <>
      <WorkspaceLayout
        header={header}
        banner={banner}
        railLabel="Library modules"
        rail={
          <TreeRail
            aria-label="Categories and modules"
            all={{ id: ALL_ID, label: 'All modules', count: counts?.total ?? tree.item_count }}
            categories={railCategories}
            activeId={moduleId ?? ALL_ID}
            dimZero={terms.length > 0}
            onSelect={(id) => selectModule(id === ALL_ID ? null : id)}
          />
        }
        toolbar={toolbar}
        footer={
          <>
            <KeyHint keys={['j', 'k']}>move</KeyHint>
            <KeyHint keys={['Enter']}>open</KeyHint>
            {editing ? <KeyHint keys={['e']}>reword (or double-click)</KeyHint> : null}
            <KeyHint keys={['/']}>search</KeyHint>
            <KeyHint keys={['Esc']}>close panel</KeyHint>
          </>
        }
        panel={
          panelRow ? (
            <LibraryPanel
              row={panelRow}
              module={panelModule}
              editing={editing && panelRow.module_id === selectedModule?.id && draftReady}
              busy={busy}
              onClose={() => setPanelId(null)}
              onCommitText={commitText}
              onRetire={retire}
              onRestore={restore}
              onKeepBoth={keepBoth}
              onDiscardDuplicate={discardDuplicate}
              onOpenHistory={(id, view) => setHistory({ moduleId: id, view })}
              onOpenSource={isCurator ? setSource : undefined}
            />
          ) : undefined
        }
      >
        {saveError || (stale && !publishOpen) ? (
          <div className="flex-none px-4 pt-3">
            <WorkspaceNotice tone={saveError ? 'critical' : 'at-risk'}>
              <div className="flex items-start gap-3">
                <span className="min-w-0 flex-1">{saveError ?? STALE_MESSAGE}</span>
                <button
                  type="button"
                  className="flex-none font-medium underline underline-offset-[3px]"
                  onClick={() => {
                    setSaveError(null);
                    setStale(false);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </WorkspaceNotice>
          </div>
        ) : null}
        {unknownModule ? (
          <EmptyInline title="Module not found">
            {isCurator && !showRetired
              ? 'This module does not exist, has not been published yet, or has been retired.'
              : 'This module does not exist, or it has not been published yet.'}
            <div className="mt-3 flex justify-center gap-2">
              <WorkspaceButton onClick={() => selectModule(null)}>Show all modules</WorkspaceButton>
              {isCurator && !showRetired ? (
                <WorkspaceButton onClick={() => setShowRetired(true)}>
                  Show retired modules
                </WorkspaceButton>
              ) : null}
            </div>
          </EmptyInline>
        ) : selectedModule && retiredSelected ? (
          <EmptyInline title={`${selectedModule.name} is retired`}>
            It is hidden from the library and from the list engagements add requirements from.
            Nothing was deleted: its {plural(selectedModule.item_count, 'requirement')}, its version
            history and its prefix <span className="font-mono">{selectedModule.prefix}</span> are
            kept, and engagements that already loaded from it are unchanged.
            {isCurator ? (
              <div className="mt-3">
                <WorkspaceButton
                  variant="primary"
                  disabled={statusMutation.isPending}
                  onClick={() => restoreModule(selectedModule)}
                >
                  <ArchiveRestore className="h-3 w-3" aria-hidden="true" />
                  Restore {selectedModule.name}
                </WorkspaceButton>
              </div>
            ) : null}
          </EmptyInline>
        ) : (
          <WorkspaceDataGrid
            aria-label={
              selectedModule ? `${selectedModule.name} requirements` : 'Library requirements'
            }
            rows={rows}
            getRowId={getRowId}
            columns={columns}
            groupBy={groupBy}
            renderGroup={renderGroup}
            renderGroupFooter={showAddRows ? renderGroupFooter : undefined}
            focusedId={focusedId}
            onFocusChange={focusRow}
            onOpen={openRow}
            wrap={wrap}
            globalKeys
            emptyState={emptyState}
            {...(draftReady
              ? {
                  isRowEditable,
                  onEditCommit,
                  rowActions,
                  rowActionsWidth: '72px',
                  rowClassName,
                  openOnClick: false,
                }
              : { isRowEditable: neverEditable })}
          />
        )}
      </WorkspaceLayout>

      <VersionHistorySheet
        target={history}
        moduleName={history ? (moduleNameById.get(history.moduleId) ?? 'Module') : ''}
        onClose={() => setHistory(null)}
      />

      {isCurator ? (
        <>
          {intake ? (
            <IntakeSheet
              // Another run is another sheet: its stage and filters start clean.
              key={intake.runId ?? 'new'}
              initialRunId={intake.runId}
              subcategoriesFor={subcategoriesFor}
              onClose={() => setIntake(null)}
              onConfirmed={onIntakeConfirmed}
              onOpenRuns={openRuns}
            />
          ) : null}
          {runsOpen ? (
            <IntakeRunsSheet
              onClose={() => setRunsOpen(false)}
              onOpenRun={(runId) => openIntake(runId)}
              onImport={() => openIntake()}
            />
          ) : null}
          <IntakeSourceViewer target={source} onClose={() => setSource(null)} />
          <RetireCategoryDialog
            open={retiringCategory != null}
            onOpenChange={(next) => {
              if (!next) setRetiringCategory(null);
            }}
            categoryName={retiringCategory?.name ?? ''}
            activeModuleNames={activeModuleNames(retiringCategory)}
            retiring={categoryStatusMutation.isPending}
            error={retireCategoryError}
            onRetire={retireCategory}
          />
          <NewModuleDialog
            open={newModuleOpen}
            onOpenChange={setNewModuleOpen}
            tree={tree}
            defaultCategoryId={selectedModule?.category_id ?? null}
            onCreated={onModuleCreated}
            onOpenExisting={openExistingModule}
          />
          {selectedModule ? (
            <>
              <RetireModuleDialog
                open={retireOpen}
                onOpenChange={setRetireOpen}
                moduleName={selectedModule.name}
                prefix={selectedModule.prefix}
                itemCount={selectedModule.item_count}
                draftChanges={changeCount}
                retiring={statusMutation.isPending}
                error={retireError}
                onRetire={retireModule}
              />
              <RenameModuleDialog
                open={renameOpen}
                onOpenChange={setRenameOpen}
                moduleId={selectedModule.id}
                moduleName={selectedModule.name}
                prefix={selectedModule.prefix}
                pendingName={renamedTo}
                tree={tree}
                saving={renameMutation.isPending}
                error={renameError}
                onRename={(name) => renameModule(name === selectedModule.name ? null : name)}
              />
              <PublishDialog
                open={publishOpen}
                onOpenChange={setPublishOpen}
                moduleName={selectedModule.name}
                currentVersion={selectedModule.current_version}
                draft={draft}
                stale={stale}
                publishing={publishMutation.isPending}
                error={publishError}
                onPublish={publish}
              />
              <AddRequirementDialog
                open={addDialogOpen}
                onOpenChange={setAddDialogOpen}
                moduleName={selectedModule.name}
                subcategories={Array.from(new Set(scopeRows.map((r) => r.subcategory)))}
                busy={busy}
                error={addError}
                onSubmit={(sub, text) => addRequirement(sub, text, 'dialog')}
              />
              {importOpen && editing ? (
                <LibraryImportSheet
                  moduleId={selectedModule.id}
                  moduleName={selectedModule.name}
                  runWrite={runWrite}
                  onClose={() => setImportOpen(false)}
                  onImported={(result) => {
                    setSaveError(null);
                    setStale(false);
                    toast({ message: libraryImportMessage(result), durationMs: 12_000 });
                  }}
                />
              ) : null}
              {tidyOpen && editing ? (
                <MetadataTidySheet
                  moduleId={selectedModule.id}
                  moduleName={selectedModule.name}
                  runWrite={runWrite}
                  onClose={() => setTidyOpen(false)}
                  onTidied={(result) => {
                    setSaveError(null);
                    setStale(false);
                    toast({
                      message: metadataTidyMessage(result, selectedModule.name),
                      durationMs: 12_000,
                    });
                  }}
                />
              ) : null}
              <WorkspaceDialog
                open={discardOpen}
                onOpenChange={setDiscardOpen}
                title={`Discard the draft of ${selectedModule.name}?`}
                description="This cannot be undone."
                actions={
                  <>
                    <WorkspaceButton size="md" onClick={() => setDiscardOpen(false)}>
                      Keep the draft
                    </WorkspaceButton>
                    <WorkspaceButton
                      size="md"
                      variant="destructive"
                      disabled={busy}
                      onClick={discardDraft}
                    >
                      Discard {plural(changeCount, 'change')}
                    </WorkspaceButton>
                  </>
                }
              >
                <p className="text-body-sm text-ink-700">
                  {plural(changeCount, 'unpublished change')} will be removed and the module goes
                  back to{' '}
                  {selectedModule.current_version > 0
                    ? `version ${selectedModule.current_version}`
                    : 'having no requirements'}
                  . Numbers given to new requirements in this draft are not reused.
                </p>
              </WorkspaceDialog>
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}

const neverEditable = () => false;

/** The modules that keep a category from being retired (rule 2). */
function activeModuleNames(category: LibraryCategory | null): string[] {
  return (category?.modules ?? []).filter((m) => m.status === 'active').map((m) => m.name);
}

/**
 * The small menu on a category row of the rail (curators only): retire an
 * active category, restore a retired one. A category with active modules
 * still offers Retire, so the confirm can say why it cannot be done.
 */
function CategoryMenu({
  category,
  onRetire,
  onRestore,
}: {
  category: LibraryCategory;
  onRetire: () => void;
  onRestore: () => void;
}) {
  const retired = category.status === 'retired';
  const inUse = activeModuleNames(category).length;
  return (
    <WorkspaceMenu
      aria-label={`${category.name} actions`}
      align="end"
      trigger={
        <button
          type="button"
          aria-label={`Actions for ${category.name}`}
          className="flex h-6 w-6 items-center justify-center rounded-chip text-ink-500 hover:bg-paper-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      }
      items={
        retired
          ? [
              {
                id: 'restore',
                label: 'Restore category',
                icon: <ArchiveRestore className="h-3.5 w-3.5" />,
                onSelect: onRestore,
              },
            ]
          : [
              {
                id: 'retire',
                label: 'Retire category…',
                icon: <Archive className="h-3.5 w-3.5" />,
                hint: inUse > 0 ? plural(inUse, 'active module') : undefined,
                onSelect: onRetire,
              },
            ]
      }
    />
  );
}

function EmptyInline({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-6 py-14 text-center text-body-sm text-ink-500">
      <strong className="mb-1 block text-h3 font-semibold text-ink-900">{title}</strong>
      {children}
    </div>
  );
}
